/** Распоред (Schedule) — the staff schedule-maker. Day / Week / Month views with
 *  prev/today/next navigation, save/load schedule templates, and click-to-add /
 *  click-to-edit shifts via <ShiftModal>. Owner/manager (route-guarded; RLS
 *  enforces writes). */
import type { ShiftTemplate } from '@gym/api-client';
import { ChevronLeft, ChevronRight, FolderOpen, Plus, Save } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, IconButton } from '../../../components/ui/Button.js';
import { CenterSpinner } from '../../../components/ui/Feedback.js';
import { SegmentedControl } from '../../../components/ui/Tabs.js';
import { toast } from '../../../components/ui/Toaster.js';
import { useLocations } from '../../../lib/locations.js';
import { type ShiftRow, useStaffList, useStaffShifts } from '../hooks/useStaff.js';

import { DayView } from './DayView.js';
import { MonthView } from './MonthView.js';
import {
  addDays,
  addMonths,
  buildTemplateEntries,
  rangeForView,
  type ScheduleView,
  startOfWeek,
} from './scheduleUtils.js';
import { type ShiftDraft, ShiftModal } from './ShiftModal.js';
import { LoadTemplateModal, SaveTemplateModal } from './TemplateModals.js';
import { useLoadTemplate, useSaveTemplate } from './useShiftTemplates.js';
import { WeekView } from './WeekView.js';

export function ScheduleTab(): ReactNode {
  const { t } = useTranslation();
  const { locations } = useLocations();
  const save = useSaveTemplate();
  const load = useLoadTemplate();

  const [view, setView] = useState<ScheduleView>('week');
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState<ShiftDraft | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);

  const { data: staff, isLoading: staffLoading } = useStaffList({ isActive: true });
  const range = rangeForView(view, anchor);
  const { data: shifts } = useStaffShifts(range);

  const staffRows = staff ?? [];
  const shiftRows = (shifts ?? []) as ShiftRow[];
  const templateEntries = buildTemplateEntries(view, anchor, shiftRows);

  function step(dir: -1 | 1): void {
    setAnchor((a) =>
      view === 'month' ? addMonths(a, dir) : addDays(a, dir * (view === 'week' ? 7 : 1)),
    );
  }

  function openAdd(staffId?: string, date?: Date): void {
    setDraft({ ...(staffId ? { staffId } : {}), date: date ?? anchor });
    setModalOpen(true);
  }
  function openEdit(shift: ShiftRow): void {
    setDraft({ shift });
    setModalOpen(true);
  }
  function pickDay(date: Date): void {
    setAnchor(date);
    setView('day');
  }

  function onSaveTemplate(name: string): void {
    save.mutate(
      { name, scope: view, entries: templateEntries },
      {
        onSuccess: () => {
          toast.success(t('staff.schedule.template_saved'));
          setSaveOpen(false);
        },
        onError: (e) => toast.error(t('common.error'), e instanceof Error ? e.message : undefined),
      },
    );
  }

  function onLoadTemplate(template: ShiftTemplate): void {
    load.mutate(
      { template, anchor, validStaffIds: new Set(staffRows.map((s) => s.id)) },
      {
        onSuccess: (n) => {
          toast.success(t('staff.schedule.template_loaded', { count: n }));
          setLoadOpen(false);
        },
        onError: (e) => toast.error(t('common.error'), e instanceof Error ? e.message : undefined),
      },
    );
  }

  const rangeLabel = ((): string => {
    if (view === 'day') {
      return anchor.toLocaleDateString(undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      });
    }
    if (view === 'week') {
      const ws = startOfWeek(anchor);
      const we = addDays(ws, 6);
      return `${ws.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} – ${we.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    return anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  })();

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <IconButton label={t('staff.schedule.day')} variant="secondary" onClick={() => step(-1)}>
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </IconButton>
          <Button variant="secondary" size="sm" onClick={() => setAnchor(new Date())}>
            {t('staff.schedule.today')}
          </Button>
          <IconButton label={t('staff.schedule.week')} variant="secondary" onClick={() => step(1)}>
            <ChevronRight className="h-4 w-4" aria-hidden />
          </IconButton>
        </div>

        <div className="text-sm font-semibold capitalize text-fg">{rangeLabel}</div>

        <div className="flex-1" />

        <SegmentedControl<ScheduleView>
          value={view}
          onChange={setView}
          options={[
            { value: 'day', label: t('staff.schedule.day') },
            { value: 'week', label: t('staff.schedule.week') },
            { value: 'month', label: t('staff.schedule.month') },
          ]}
        />

        <Button
          variant="secondary"
          size="sm"
          icon={<FolderOpen className="h-4 w-4" />}
          onClick={() => setLoadOpen(true)}
        >
          {t('staff.schedule.load_template')}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          icon={<Save className="h-4 w-4" />}
          disabled={templateEntries.length === 0}
          onClick={() => setSaveOpen(true)}
        >
          {t('staff.schedule.save_template')}
        </Button>
        <Button
          variant="brand"
          size="sm"
          icon={<Plus className="h-4 w-4" />}
          onClick={() => openAdd(undefined, view === 'month' ? new Date() : anchor)}
        >
          {t('staff.schedule.add_shift')}
        </Button>
      </div>

      {staffLoading ? (
        <CenterSpinner />
      ) : view === 'week' ? (
        <WeekView
          anchor={anchor}
          staff={staffRows}
          shifts={shiftRows}
          onAdd={openAdd}
          onEdit={openEdit}
        />
      ) : view === 'day' ? (
        <DayView
          anchor={anchor}
          staff={staffRows}
          shifts={shiftRows}
          onAdd={openAdd}
          onEdit={openEdit}
        />
      ) : (
        <MonthView
          anchor={anchor}
          staff={staffRows}
          shifts={shiftRows}
          onPickDay={pickDay}
          onEdit={openEdit}
        />
      )}

      <ShiftModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        staff={staffRows}
        locations={locations}
        draft={draft}
      />
      <SaveTemplateModal
        open={saveOpen}
        onOpenChange={setSaveOpen}
        count={templateEntries.length}
        saving={save.isPending}
        onSubmit={onSaveTemplate}
      />
      <LoadTemplateModal
        open={loadOpen}
        onOpenChange={setLoadOpen}
        scope={view}
        loading={load.isPending}
        onLoad={onLoadTemplate}
      />
    </div>
  );
}
